import React, { useContext, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@vimily/bonjoro-ui-components';
import CreditCardForm, {
  CreditCardFormDefaults,
  CreditCardInputError,
} from '../Components/CreditCardForm';
import Mandate from '../Components/Mandate';
import CreditCard from '../Components/CreditCard';
import { CardElement, Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElements } from '@stripe/stripe-js';
import { StripeCardElement } from '@stripe/stripe-js';
import FlashContext from '~/App/Components/Flash/Context';
import { BillingDetails } from '~/types/stripe';
import useCreditCard from '../hooks/useCreditCard';
import { useCreditCardsQuery } from '../api';
import { Link } from 'react-router-dom';
import analytics from '~/utils/Analytics';
import { BILLING_UPDATE_CARD_SUBMIT } from '~/generated/AnalyticsTypes';
import { useQueryString } from '~/react-hooks/use-query-string';
import { useQueryClient } from 'react-query';
import { queryKeys } from '../../NewBilling/Hooks/useCreditCard';
import { queryKeys as subscriptionQueryKeys } from '../../NewBilling/Hooks/useSubscription';
import { queryKeys as appConfigKeys } from '~/App/Api/AppConfig';

const App = () => {
  const { createFlash } = useContext(FlashContext);
  const { ref, price } = useQueryString<{ ref: string; price: number }>();
  const queryClient = useQueryClient();

  const isFromTeamSettingsPage = ref === 'team';

  const {
    elements,
    isConfirmingCreditCard,
    confirmCreditCardMutation,
    isCreatingCreditCard,
    createCreditCardMutation,
  } = useCreditCard();

  useEffect(() => {
    if (isConfirmingCreditCard) {
      createFlash({
        id: 'processing',
        title: 'Processing',
        message: 'Please wait',
      });
    }
  }, [isConfirmingCreditCard]);

  const [confirmCardInputError, setConfirmCardInputError] =
    useState<CreditCardInputError>();

  const { data: creditCards } = useCreditCardsQuery();

  const doSubmit = async (details: BillingDetails) => {
    const stripeCardElement = (elements as StripeElements).getElement(
      CardElement
    ) as StripeCardElement;

    const result = await confirmCreditCardMutation({
      billingDetails: details,
      cardElement: stripeCardElement,
    });

    if (result.error) {
      setConfirmCardInputError({
        setup_intent_id: result.error.setup_intent?.id as string,
        message: result.error.message as string,
      });
    }

    if (result.setupIntent) {
      await createCreditCardMutation({ intent_id: result.setupIntent.id });
      stripeCardElement.clear();

      await queryClient.invalidateQueries(queryKeys.detail);
      await queryClient.invalidateQueries(subscriptionQueryKeys.lists());
      await queryClient.invalidateQueries(appConfigKeys.detail);

      createFlash({
        id: 'update-credit-card',
        title: 'Success',
        message: 'Credit card updated successfully',
      });

      await analytics.track(BILLING_UPDATE_CARD_SUBMIT, {
        status: result.setupIntent.status,
      });

      if (isFromTeamSettingsPage) {
        window.location.href = document.referrer;
      } else {
        window.location.href = '/billing';
      }
    }
  };

  const subheading = useMemo(() => {
    if (isFromTeamSettingsPage && price !== undefined) {
      return (
        <strong className='mb-5 block text-base'>
          You will be charged an additional ${Number(price).toFixed(2)} per
          additional team member once the invite is accepted. Kindly provide
          your payment information to add more team members.
        </strong>
      );
    }
  }, [ref, price]);

  return (
    <div className='w-full max-w-screen-md'>
      <h2 className='text-2xl mb-4 font-bold text-neutral-800'>Update card</h2>
      {subheading}
      {creditCards &&
        creditCards.data.length > 0 &&
        creditCards.data.map(
          (card) =>
            card.is_default && (
              <CreditCard
                id={card.id}
                expiryMonth={card.exp_month}
                expiryYear={card.exp_year}
                brand={card.brand}
                last4={card.last4}
                key={card.id}
                className='mb-4'
              />
            )
        )}
      <CreditCardForm
        onSubmit={doSubmit}
        className='w-full'
        creditCardInputError={confirmCardInputError}
      />
      <Mandate className='mb-4' />
      <div className='flex justify-end items-center'>
        <Link to='/billing'>
          <Button variant='secondary' className='mr-4'>
            Cancel
          </Button>
        </Link>
        <Button
          disabled={isCreatingCreditCard || isConfirmingCreditCard}
          form={CreditCardFormDefaults.ID}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export const UpdateCreditCardApp = () => {
  const [stripePromise] = useState(() => loadStripe(window.Bonjoro.stripeKey));

  return (
    <Elements stripe={stripePromise}>
      <App />
    </Elements>
  );
};

const UpdateCard = () => {
  const targetElement = document.getElementById('bonjoro-app') as HTMLElement;
  return createPortal(<UpdateCreditCardApp />, targetElement);
};

export default UpdateCard;
